// Generated by GitHub Copilot
// API service for self-service feature flag management

import { authFetch } from "../utils/authFetch";

/**
 * Security configuration for feature flag operations
 */
const SECURITY_CONFIG = {
  // Features that are not allowed for self-service (admin-only)
  BLACKLISTED_FEATURES: [
    "admin",
    "deity",
    "system",
    "debug",
    "backdoor",
    "maintenance",
    "override",
    "superuser",
    "escalation",
    "billing",
    "audit",
    "compliance",
  ],

  // Maximum retry attempts for failed requests
  MAX_RETRIES: 3,

  // Rate limiting configuration
  RATE_LIMIT: {
    maxRequestsPerMinute: 10,
    windowSizeMs: 60000,
  },

  // Request timeout configuration
  TIMEOUT_MS: 30000,
};

/**
 * Client-side rate limiting to prevent abuse
 */
class RateLimiter {
  constructor(maxRequests, windowMs) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = [];
  }

  isAllowed() {
    const now = Date.now();
    // Remove old requests outside the window
    this.requests = this.requests.filter((time) => now - time < this.windowMs);

    if (this.requests.length >= this.maxRequests) {
      return false;
    }

    this.requests.push(now);
    return true;
  }

  getTimeUntilReset() {
    if (this.requests.length === 0) return 0;
    const oldestRequest = Math.min(...this.requests);
    return Math.max(0, this.windowMs - (Date.now() - oldestRequest));
  }
}

// Global rate limiter instance
const rateLimiter = new RateLimiter(
  SECURITY_CONFIG.RATE_LIMIT.maxRequestsPerMinute,
  SECURITY_CONFIG.RATE_LIMIT.windowSizeMs
);

/**
 * Validates feature flag name for security
 * @param {string} feature - Feature flag name to validate
 * @throws {Error} If feature is invalid or blacklisted
 */
const validateFeatureForSelfService = (feature) => {
  // Check if feature name is valid
  if (!feature || typeof feature !== "string") {
    throw new Error("Feature name must be a non-empty string");
  }

  // Check length constraints
  if (feature.length > 50) {
    throw new Error("Feature name too long (max 50 characters)");
  }

  // Check for valid characters (alphanumeric, underscore, hyphen)
  if (!/^[a-zA-Z0-9_-]+$/.test(feature)) {
    throw new Error("Feature name contains invalid characters");
  }

  // Check if feature is blacklisted for self-service
  if (SECURITY_CONFIG.BLACKLISTED_FEATURES.includes(feature.toLowerCase())) {
    throw new Error(
      `Feature '${feature}' is not available for self-service modification`
    );
  }
};

/**
 * Sanitizes input to prevent injection attacks
 * @param {string} input - Input to sanitize
 * @returns {string} Sanitized input
 */
const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;

  // Remove potentially dangerous characters
  return input
    .replace(/[<>]/g, "") // Remove angle brackets
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/data:/gi, "") // Remove data: protocol
    .trim();
};

/**
 * Set a feature flag for the current user with enhanced security
 * @param {string} feature - The feature flag name (e.g., 'onepagesummary')
 * @param {boolean} enabled - Whether to enable or disable the feature
 * @returns {Promise<Object>} API response
 */
export const setMyFeatureFlag = async (feature, enabled) => {
  try {
    // Client-side security validation
    validateFeatureForSelfService(feature);

    // Check rate limiting
    if (!rateLimiter.isAllowed()) {
      const timeUntilReset = Math.ceil(rateLimiter.getTimeUntilReset() / 1000);
      throw new Error(
        `Rate limit exceeded. Please wait ${timeUntilReset} seconds before trying again.`
      );
    }

    // Sanitize inputs
    const sanitizedFeature = sanitizeInput(feature);

    // Validate enabled parameter
    if (typeof enabled !== "boolean") {
      throw new Error("Enabled parameter must be a boolean value");
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      SECURITY_CONFIG.TIMEOUT_MS
    );

    const response = await authFetch(
      `/api/v1/config/me/features/${encodeURIComponent(sanitizedFeature)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest", // CSRF protection
        },
        credentials: "include", // Include session cookies
        signal: controller.signal,
        body: JSON.stringify({
          feature: sanitizedFeature,
          enabled: enabled,
        }),
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: "Unknown error occurred",
      }));

      // Enhanced error handling based on status codes
      switch (response.status) {
        case 401:
          throw new Error("Authentication required. Please log in again.");
        case 403:
          throw new Error(
            errorData.error ||
              "Access denied. You may not have permission to modify feature flags."
          );
        case 404:
          throw new Error("User profile not found. Please contact support.");
        case 429:
          throw new Error(
            "Too many requests. Please wait before trying again."
          );
        case 500:
          throw new Error("Server error. Please try again later.");
        default:
          throw new Error(
            errorData.error || `HTTP ${response.status}: ${response.statusText}`
          );
      }
    }

    const result = await response.json();

    // Validate response structure
    if (!result || typeof result !== "object") {
      throw new Error("Invalid response format from server");
    }

    // Log successful operation for security audit
    console.log(
      `Feature flag '${sanitizedFeature}' ${
        enabled ? "enabled" : "disabled"
      } successfully`
    );

    return result;
  } catch (error) {
    // Security logging for failed attempts
    console.error("Feature flag operation failed:", {
      feature: feature,
      enabled: enabled,
      error: error.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Get current user's feature flag states from their profile
 * Note: This uses the existing profile endpoint since feature flags are part of user profile
 * @returns {Promise<Object>} User profile with feature flags
 */
export const getMyProfile = async () => {
  try {
    // Generated by GitHub Copilot - Use authFetch to handle 401 errors globally
    const response = await authFetch("/api/v1/session", {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const sessionData = await response.json();
    return sessionData;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

/**
 * Retry mechanism for API calls with exponential backoff
 * @param {Function} apiCall - The API function to retry
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Result of the API call
 */
export const retryApiCall = async (
  apiCall,
  maxRetries = 3,
  baseDelay = 1000
) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;

      // Don't retry on client errors (4xx) except 408 (timeout)
      if (
        error.message.includes("HTTP 4") &&
        !error.message.includes("HTTP 408")
      ) {
        throw error;
      }

      if (attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(
          `API call failed (attempt ${attempt + 1}/${
            maxRetries + 1
          }), retrying in ${delay}ms:`,
          error.message
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};
